/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box } from 'chakra-v3';
import { Text } from '../Text';
import { Button } from '../buttons/Index';
import { Link } from '../Link';
import type { BoxProps } from 'chakra-v3';
import { NavLink } from 'react-router-dom';

type ButtonProps = React.ComponentProps<typeof Button>;

/**
 * Props for a navigation link menu item
 */
export interface NavLink {
  /** Display label for the menu item */
  label: string;
  /** Navigation path or external URL */
  path: string;
  /** Icon element to display */
  icon: React.ReactElement;
  /** Whether this link opens in a new tab */
  external?: boolean;
}

/**
 * Props for the MenuItem component
 */
interface MenuItemProps extends BoxProps {
  /** Navigation link configuration */
  link: NavLink;
  /** Callback when item is clicked */
  onClick?: (e?: React.MouseEvent) => void;
  /** Whether this menu item is currently active (matches current route) */
  isActive?: boolean;
  /** Props to pass to the Button component when the item is active */
  buttonProps?: ButtonProps;
}

/**
 * Reusable menu item component for both desktop and mobile navigation.
 * When active, transforms into a secondary button using the Button component.
 *
 * @example
 * ```tsx
 * <MenuItem
 *   link={{
 *     label: 'Features',
 *     path: '/features',
 *     icon: <FeatureIcon />
 *   }}
 *   onClick={() => console.log('clicked')}
 *   isActive={true}
 * />
 * ```
 */
export const MenuItem: React.FC<MenuItemProps> = ({ link, onClick, isActive = false, buttonProps, ...boxProps }) => {
  const handleClick = (e?: React.MouseEvent) => {
    if (link.external && typeof window !== 'undefined') {
      window.open(link.path, '_blank', 'noopener,noreferrer');
    }
    onClick?.(e);
  };

  const menuContent = (
    <>
      <Box w="24px" h="24px" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
        {link.icon}
      </Box>
      <Text whiteSpace="nowrap">{link.label}</Text>
    </>
  );

  // Common styling props for menu item
  const menuItemProps = {
    display: 'flex' as const,
    gap: '12px',
    alignItems: 'center' as const,
    px: '12px',
    py: '8px',
    ...boxProps,
  };

  // When active, use Button component with secondary variant
  if (isActive) {
    if (link.external) {
      return (
        <Button
          variant="secondary"
          onClick={handleClick}
          display="flex"
          gap="12px"
          alignItems="center"
          px="12px"
          py="8px"
          {...buttonProps}
        >
          {menuContent}
        </Button>
      );
    }

    if (link.path.includes('#')) {
      return (
        <Button
          asChild
          variant="secondary"
          display="flex"
          gap="12px"
          alignItems="center"
          px="12px"
          py="8px"
          {...buttonProps}
        >
          <Link href={link.path} variant="plain" onClick={(e) => onClick?.(e)}>
            {menuContent}
          </Link>
        </Button>
      );
    }

    return (
      <Button
        asChild
        variant="secondary"
        display="flex"
        gap="12px"
        alignItems="center"
        px="12px"
        py="8px"
        {...buttonProps}
      >
        <NavLink to={link.path} onClick={(e) => onClick?.(e)}>
          {menuContent}
        </NavLink>
      </Button>
    );
  }

  // When inactive, use regular Box-based menu item
  const inactiveMenuItem = (
    <Box
      as="span"
      bg="transparent"
      border="none"
      color="text.primary"
      cursor="pointer"
      _hover={{ opacity: 0.8 }}
      transition="opacity 0.2s"
      fontFamily="body"
      fontSize="16px"
      fontWeight={400}
      lineHeight="1.4"
      letterSpacing="0.08px"
      _focusVisible={{ outline: 'none' }}
      {...menuItemProps}
    >
      {menuContent}
    </Box>
  );

  // Wrap in appropriate link component based on link type
  if (link.external) {
    return (
      <Box as="button" onClick={handleClick} bg="transparent" border="none" p={0}>
        {inactiveMenuItem}
      </Box>
    );
  }

  if (link.path.includes('#')) {
    return (
      <Link href={link.path} variant="plain" onClick={(e) => onClick?.(e)}>
        {inactiveMenuItem}
      </Link>
    );
  }

  return (
    <Link asChild variant="plain">
      <NavLink
        to={link.path}
        onClick={(e) => {
          onClick?.(e);
        }}
      >
        {inactiveMenuItem}
      </NavLink>
    </Link>
  );
};
